import { ReactComponent as AlmaLinuxIcon } from '../assets/icons/alma-linux.svg';
import { ReactComponent as AmazonLinuxIcon } from '../assets/icons/amazon-linux.svg';
import { ReactComponent as CentOSIcon } from '../assets/icons/centos.svg';
import { ReactComponent as DebianIcon } from '../assets/icons/debian.svg';
import { ReactComponent as FedoraIcon } from '../assets/icons/fedora.svg';
import { ReactComponent as KubernetesIcon } from '../assets/icons/kubernetes.svg';
import { ReactComponent as MacOSIcon } from '../assets/icons/macos.svg';
import { ReactComponent as OracleLinuxIcon } from '../assets/icons/oracle-linux.svg';
import { ReactComponent as AmiIcon } from '../assets/icons/ami.svg';
import { ReactComponent as PackerIcon } from '../assets/icons/packer.svg';
import { ReactComponent as LayersIcon } from '../assets/icons/layers.svg';
import { ReactComponent as MaintenanceIcon } from '../assets/icons/maintenance.svg';
import { ReactComponent as RedHatIcon } from '../assets/icons/redhat.svg';
import { ReactComponent as RockyLinuxIcon } from '../assets/icons/rocky-linux.svg';
import { ReactComponent as SuseIcon } from '../assets/icons/suse.svg';
import { ReactComponent as UbuntuIcon } from '../assets/icons/ubuntu.svg';
import { ReactComponent as WindowsServerIcon } from '../assets/icons/windows-server.svg';

// Map blueprint name prefixes to their icon component and display name
const OS_MAP = [
  { match: /^almalinux/i, icon: AlmaLinuxIcon, label: 'AlmaLinux' },
  { match: /^amazonlinux/i, icon: AmazonLinuxIcon, label: 'Amazon Linux' },
  { match: /^centos/i, icon: CentOSIcon, label: 'CentOS' },
  { match: /^debian/i, icon: DebianIcon, label: 'Debian' },
  { match: /^eks[/-]/i, icon: KubernetesIcon, label: 'EKS' },
  { match: /^fedora/i, icon: FedoraIcon, label: 'Fedora' },
  { match: /kubernetes/i, icon: KubernetesIcon, label: 'Kubernetes' },
  { match: /^macos/i, icon: MacOSIcon, label: 'macOS' },
  { match: /^opensuse/i, icon: SuseIcon, label: 'openSUSE' },
  { match: /^oracle/i, icon: OracleLinuxIcon, label: 'Oracle Linux' },
  { match: /^redhat|^rhel/i, icon: RedHatIcon, label: 'Red Hat' },
  { match: /^rocky/i, icon: RockyLinuxIcon, label: 'Rocky Linux' },
  { match: /^suse/i, icon: SuseIcon, label: 'SUSE' },
  { match: /^ubuntu/i, icon: UbuntuIcon, label: 'Ubuntu' },
  { match: /^win-server|^windows/i, icon: WindowsServerIcon, label: 'Windows Server' },
];

/**
 * Get icon component and display label for a blueprint name or OS type.
 * @param {string} name - Blueprint name (e.g. "ubuntu24.04-lts") or OS type (e.g. "linux", "windows")
 * @returns {{ icon: React.ComponentType, label: string } | null}
 */
export function getOsInfo(name) {
  if (!name) return null;
  // Strip common prefixes from workflow names (e.g. "packer-build-ubuntu24.04-lts-..." → "ubuntu24.04-lts-...")
  const normalized = name.replace(/^packer-build-/, '');
  for (const entry of OS_MAP) {
    if (entry.match.test(normalized)) {
      return { icon: entry.icon, label: entry.label };
    }
  }
  return null;
}

/**
 * Get just the icon component for a blueprint name or OS type.
 */
export function getOsIcon(name) {
  return getOsInfo(name)?.icon || null;
}

/**
 * Get the display label for a blueprint name.
 */
export function getOsLabel(name) {
  return getOsInfo(name)?.label || name;
}

/**
 * Format architecture for display. Maps GraphQL enum values to AWS-style display.
 * Matches AWS EC2 API: i386, x86_64, arm64, x86_64_mac, arm64_mac
 */
export function formatArch(arch, verbose = false) {
  if (!arch) return verbose ? 'x86_64 (Intel/AMD)' : 'x86_64';
  const lower = arch.toLowerCase();
  const labels = {
    i386:        { short: 'i386',        long: 'i386 (32-bit legacy)' },
    x86_64:      { short: 'x86_64',      long: 'x86_64 (Intel/AMD)' },
    arm64:       { short: 'arm64',       long: 'arm64 (Graviton)' },
    x86_64_mac:  { short: 'x86_64_mac',  long: 'x86_64_mac (Intel Mac)' },
    arm64_mac:   { short: 'arm64_mac',   long: 'arm64_mac (Apple Silicon)' },
  };
  const entry = labels[lower] || labels['x86_64'];
  return verbose ? entry.long : entry.short;
}

export { PackerIcon, AmiIcon, LayersIcon, MaintenanceIcon };

/**
 * Label for a schedule's Blueprint column.
 *
 * Maintenance and matrix CronWorkflows carry no blueprint-name parameter, so the
 * column was blank (Dashboard) or a bare dash (Schedules page) for three of four
 * rows. Shared so the two views cannot drift apart again.
 */
export function scheduleBlueprintLabel(name = '') {
  if (name.includes('build-all') || name.includes('matrix')) return 'All blueprints';
  if (name.includes('cleanup') || name.includes('sweeper')) return 'Maintenance';
  return null;
}

/**
 * Icon for a schedule that has no single blueprint, so the Blueprint column is
 * a consistent icon+label everywhere instead of one logo and three bare strings.
 */
export function scheduleBlueprintIcon(name = '') {
  if (name.includes('build-all') || name.includes('matrix')) return LayersIcon;
  if (name.includes('cleanup') || name.includes('sweeper')) return MaintenanceIcon;
  return null;
}
